import { CampoValor, DadosPendenciaCriada } from "./types";
import { mapearAnexos } from "../../Compartilhado/utils";
import type { Email } from "../DemandaCriada/types";
import { formatCurrency } from "../../../../../../../utility/currencyUtils";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";
import { mapearEmail } from "../../../../../../../utility/historicoLogUtils";
import { getStatusLabelStatusKanban } from "../../../../../../../utility/statusPendenciaKanban";
import { formatTipo } from "../../../../../../../utility/pendenciaTipos";

const formatDate = (value: string | Date | null | undefined, includeTimezone?: string): string => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '—';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const dateStr = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        return includeTimezone ? `${dateStr} ${includeTimezone}` : dateStr;
    } catch {
        return '—';
    }
};

const getTimezoneLabel = (dataTz: string | null | undefined): string => {
    if (!dataTz) return '';
    const tzMap: Record<string, string> = {
        'BRT': 'BRT (UTC-3) - Brasília',
        'AMT': 'AMT (UTC-4) - Manaus',
        'ACT': 'ACT (UTC-5) - Rio Branco',
        'FNT': 'FNT (UTC-2) - Fernando de Noronha',
        'BRST': 'BRST (UTC-2) - Brasília',
        'UTC': 'UTC (UTC+0)',
    };
    return tzMap[dataTz] || `${dataTz} (UTC)`;
};

const getOrigemLabel = (origem: string): string => {
    const origemMap: Record<string, string> = {
        'Solicitante': 'Solicitante',
        'Correspondente': 'Profissional',
        'Operador': 'Operador'
    };
    return origemMap[origem] || origem;
};

const getDestinoLabel = (destino: string): string => {
    const destinoMap: Record<string, string> = {
        'solicitante': 'Solicitante',
        'profissional': 'Profissional',
        'operador': 'Operador'
    };
    return destinoMap[destino] || destino;
};

export const mapearDadosPendencia = (logPendencia: any, dadosExtra: any): CampoValor[] => {
    if (!logPendencia || !logPendencia.dados_depois) {
        return [];
    }

    const dadosDepois = parseJsonSafely(logPendencia.dados_depois);
    const extra = parseJsonSafely(dadosExtra);
    
    const campos: CampoValor[] = [];

    if (dadosDepois.tipo) {
        campos.push({
            campo: 'Tipo',
            valor: formatTipo(dadosDepois.tipo)
        });
    }

    if (dadosDepois.descricao) {
        campos.push({
            campo: 'Descrição',
            valor: dadosDepois.descricao
        });
    }

    if (extra?.valor || dadosDepois.valor) {
        const valor = extra?.valor || dadosDepois.valor;
        campos.push({
            campo: 'Valor',
            valor: formatCurrency(valor)
        });
    }

    // Campo Criado por: nome da pessoa (tipo)
    const origemTipo = extra?.origem ? getOrigemLabel(extra.origem) : 'Operador';
    if (extra?.nome_origem) {
        campos.push({
            campo: 'Criado por',
            valor: `${extra.nome_origem} (${origemTipo})`
        });
    } else {
        campos.push({
            campo: 'Criado por',
            valor: `(${origemTipo})`
        });
    }

    // Campo Destinatário: nome da pessoa (tipo)
    const destinoTipo = extra?.destino || dadosDepois.destino ? getDestinoLabel(extra?.destino || dadosDepois.destino) : 'Operador';
    if (extra?.nome_destino) {
        campos.push({
            campo: 'Destinatário',
            valor: `${extra.nome_destino} (${destinoTipo})`
        });
    } else {
        campos.push({
            campo: 'Destinatário',
            valor: `(${destinoTipo})`
        });
    }

    if (dadosDepois.status_kanban) {
        campos.push({
            campo: 'Status Inicial',
            valor: getStatusLabelStatusKanban(dadosDepois.status_kanban)
        });
    }

    if (dadosDepois.criado) {
        const timezoneCode = extra?.data_tz || 'BRT';
        campos.push({
            campo: 'Data de Criação',
            valor: formatDate(dadosDepois.criado, timezoneCode)
        });
    }

    return campos;
};

export const processarDadosPendenciaCriada = (logs: any[]): DadosPendenciaCriada => {
    const logPendencia = logs.find((log: any) => log.entidade_nome === 'pendencia');
    
    if (!logPendencia) {
        return {
            campos: [],
            anexos: [],
            email: null,
            idPendencia: undefined
        };
    }

    const dadosExtra = parseJsonSafely(logPendencia.dados_extra);
    const campos = mapearDadosPendencia(logPendencia, dadosExtra);
    const anexos = mapearAnexos(logs);
    const email = mapearEmail(logs);

    return {
        campos,
        anexos,
        email,
        idPendencia: logPendencia.entidade_id
    };
};

